
import { Canvas } from "fabric";
import { CustomCanvas } from "@/types/fabric";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeText } from "@/utils/sanitization";

interface ValidationResult {
  success: boolean;
  message?: string;
  conflictType?: 'minor' | 'major';
  serverVersion?: number;
}

export const useValidation = () => {
  const validateDrawingData = (canvas: Canvas | null): ValidationResult => {
    if (!canvas) {
      return {
        success: false,
        message: "Unable to prepare drawing data.",
      };
    }
    
    try {
      // Check if canvas can be serialized properly
      const testSerialization = JSON.stringify(canvas.toJSON());
      if (!testSerialization) {
        return {
          success: false,
          message: "Unable to prepare drawing data for saving.",
        };
      }
    } catch (error) {
      console.error("Canvas serialization error:", error);
      return {
        success: false,
        message: "Unable to process drawing data. Please try again.",
      };
    }
    
    return { success: true };
  };

  const checkVersionConflict = async (drawingId: string, currentVersion: number): Promise<ValidationResult> => {
    if (!drawingId) {
      return {
        success: false,
        message: "Invalid drawing ID.",
      };
    }
    
    try {
      const { data: currentDrawing, error: fetchError } = await supabase
        .from("whiteboard_history")
        .select('version')
        .eq('id', drawingId)
        .maybeSingle();

      if (fetchError) {
        console.error("Version conflict check error:", fetchError);
        return {
          success: false,
          message: "Unable to verify drawing version.",
        };
      }

      if (!currentDrawing) {
        return {
          success: false,
          message: "Drawing not found. It may have been deleted.",
        };
      }

      if (currentDrawing.version !== currentVersion) {
        console.warn("Version conflict detected:", { 
          currentVersion, 
          serverVersion: currentDrawing.version 
        });
        
        // Determine the conflict type based on version difference
        const versionDiff = currentDrawing.version - currentVersion;
        const conflictType = versionDiff <= 1 ? 'minor' : 'major';
        
        return {
          success: false,
          message: "This drawing has been modified elsewhere. Please reload the latest version.",
          conflictType,
          serverVersion: currentDrawing.version
        };
      }

      return { success: true };
    } catch (error) {
      console.error("Error checking version:", error);
      return {
        success: false,
        message: "Unable to verify drawing version.",
      };
    }
  };

  const handleSaveError = (error: any): ValidationResult => {
    console.error("Save error:", error);
    
    if (!navigator.onLine) {
      return {
        success: false,
        message: "You appear to be offline. Please check your internet connection and try again.",
      };
    }
    
    if (error.code === 'PGRST116') {
      return {
        success: false,
        message: "This drawing has been modified elsewhere. Please reload the latest version.",
        conflictType: 'minor'
      };
    }
    
    if (error.code === '42501' || error.message?.includes('permission')) {
      return {
        success: false,
        message: "You don't have permission to save this drawing.",
      };
    }
    
    return {
      success: false,
      message: error.message || "Unable to save drawing. Please try again.",
    };
  };

  return {
    validateDrawingData,
    checkVersionConflict,
    handleSaveError,
  };
};
